/**
 * Web entry point: TanStack Query + the data router (docs/08). Route tree and
 * guards live in app/router.tsx; the manifest in app/routes.manifest.ts is the
 * machine-checked mirror (gate 9).
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';

import './design/theme.css';
import { router } from './app/router.js';
import { installStaleBuildRecovery } from './app/staleBuild.js';
import { applyCachedTheme } from './app/theme.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

// Before anything renders: a chunk that vanished under an open tab must not be
// able to leave the app dead (app/staleBuild.ts).
installStaleBuildRecovery();

// Before the first paint: replay the cached theme choice so a person who picked
// Light on a dark laptop does not watch the app load dark (app/theme.ts). The
// server's answer confirms or corrects it once the shell mounts.
applyCachedTheme();

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
